import { useEffect, useState, ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';
import { Form, FormFeedback, FormGroup, Input } from 'reactstrap';
import { validOrgName, validOrgNameErrorMessage } from '../../../../utils/commons';
import { IHeader, IHeaderInput } from '../../../../types/infogo';
import Textarea, { TextareaHandler } from '../../../common/textarea/textarea';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';
import { ApolloError } from '@apollo/client';

export type HeaderHandler = {
    getHeader: () => IHeaderInput;
    validate: (inclMandatoryCheck: boolean) => boolean;
    setError: (error: ApolloError) => ApolloError;
}

type HeaderDetailsProps = {
    header?: IHeader | null;
    onChange: () => void;
    autoFocus?: boolean;
}

const HeaderDetails = forwardRef<HeaderHandler, HeaderDetailsProps>((props: HeaderDetailsProps, ref: ForwardedRef<any>) => {
    const { header, onChange, autoFocus } = props

    const [headerInput, setHeaderInput] = useState<Partial<IHeaderInput>>()

    const descRef = useRef<TextareaHandler>(null)

    const [nameError, setNameError] = useState<string>('')

    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current && frenchTranslatorAutoFocusCheck()) {
            inputRef.current.focus();
        }
    })

    useEffect(() => {
        setHeaderInput({
            name: header?.name || '',
            nameFrench: header?.nameFrench || '',
            nameFrenchSync: header?.nameFrenchSync || 0,
            description: header?.description || '',
            descriptionFrench: header?.descriptionFrench || '',
            descriptionFrenchSync: header?.descriptionFrenchSync || 0
        })
    }, [header])

    const frenchTranslatorAutoFocusCheck = (): boolean => {
        return !!autoFocus && !!isFrenchTranslator && !!headerInput?.nameFrench;
    }

    useImperativeHandle(ref, () => ({
        getHeader() {
            return {
                ...headerInput,
                name: headerInput?.name?.trim()
            }
        },
        validate(inclMandatoryCheck: boolean) {
            let valid: boolean = !nameError && !!descRef.current?.validate()

            if (valid && inclMandatoryCheck) {
                if (!headerInput?.name?.trim()) {
                    setNameError("Name is required.")
                    return false
                }
            }
            return valid
        },
        setError(error: ApolloError) {
            if (!error) return;

            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                error.graphQLErrors.forEach((graphQLError) => {
                    // if this happens, there might be a validation mismatch between the client and server
                    if (graphQLError.extensions && graphQLError.extensions.field === "headerName") {
                        setNameError(error.message);
                    }
                });
            }
        }
    }), [headerInput])

    useEffect(() => {
        onChange()
    }, [headerInput])

    const validiateName = (name: string | undefined): boolean => {
        if (name && name.length > 200) {
            setNameError("This value is too long. It should have 200 characters or fewer.")
            return false
        } else if (name && !validOrgName(name)) {
            setNameError(validOrgNameErrorMessage)
            return false
        }
        setNameError('')
        return true
    }

    const changeName = (event: any) => {
        validiateName(event.target.value)
        setHeaderInput({ ...headerInput, name: event.target.value, nameFrenchSync: 0 })
    }

    const changeNameTranslated = (event: any) => {
        validiateName(event.target.value)
        setHeaderInput({ ...headerInput, nameFrenchSync: event.target.checked ? 1 : 0 })
    }

    const changeDescription = (value: string) => {
        setHeaderInput({ ...headerInput, description: value, descriptionFrenchSync: 0 })
    }

    const changeDescriptionTranslated = (event: any) => {
        validiateName(event.target.value)
        setHeaderInput({ ...headerInput, descriptionFrenchSync: event.target.checked ? 1 : 0 })
    }

    return (
        <Form className="oms-container">

            <div className='row'>
                <div className='col-md-3'><label htmlFor="headerName">Name: (required)</label></div>
                {!isFrenchTranslator && <div className='col-md-9'>
                    <Input id="headerName"
                        type="text"
                        value={headerInput?.name}
                        placeholder='Enter a name for the header'
                        onChange={changeName}
                        style={{ width: '100%' }}
                        required
                        invalid={!!nameError}
                        autoFocus={autoFocus}
                    />
                    <FormFeedback>{nameError}</FormFeedback>
                </div>
                }
                {isFrenchTranslator && <div className='col-md-9'><span id="headerName"></span>{headerInput?.name} </div>}
            </div>
            {frenchState.isFrenchChecked && <>
                <div className='row'>
                    <div className='col-md-3'><label htmlFor="headerNameF">Header Name (French):</label></div>
                    <div className='col-md-9'><span id="headerNameF" lang="fr">{headerInput?.nameFrench}</span></div>
                </div>
                <div className='row' style={{ 'marginBottom': '10px', 'marginTop': '10px' }}>
                    <div className='col-md-12'>
                        <Input id="nameFrenchSync" type="checkbox" checked={headerInput?.nameFrenchSync === 1 ? true : false} onChange={changeNameTranslated} disabled={!headerInput?.nameFrench} innerRef={inputRef} /> {' '}
                        <label htmlFor="nameFrenchSync">Translated Header Name</label>
                    </div>
                </div>
            </>
            }
            <div className='row'>
                <div className='col-md-3'><label htmlFor="headerDesc">Description: </label></div>
                {!isFrenchTranslator && <div className='col-md-9'>
                    <Textarea
                        id="headerDesc"
                        ref={descRef}
                        maxLength={1500}
                        rows={3}
                        value={headerInput?.description}
                        onValueChange={changeDescription}
                        placeholder='Enter a description for the header'>
                    </Textarea>
                </div>
                }
                {isFrenchTranslator && <div className='col-md-9'><span id="headerDesc"></span>{headerInput?.description} </div>}
            </div>
            {frenchState.isFrenchChecked && <>
                <div className='row'>
                    <div className='col-md-3'><label htmlFor="headerDescF">Header Description  (French):</label></div>
                    <div className='col-md-9'><span id="headerDescF" lang="fr">{headerInput?.descriptionFrench}</span></div>
                </div>
                <div className='row'>
                    <div className='col-md-12'>
                        <Input id="descFrenchSync" type="checkbox" checked={headerInput?.descriptionFrenchSync === 1 ? true : false} onChange={changeDescriptionTranslated} disabled={!headerInput?.descriptionFrench} /> {' '}
                        <label htmlFor="descFrenchSync">Translated Header Description </label>
                    </div>
                </div>
            </>
            }

        </Form>
    )
})

export default HeaderDetails
